"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { toCsv, toTsv } from "@/lib/tool-kit/research-design/matrix";
import type {
  BudgetResult,
  YearBudget,
} from "@/lib/tool-kit/research-ops/grant-budget";

export interface GrantResultLabels {
  currency: string;
  statDirect: string;
  statIndirect: string;
  statTotal: string;
  colItem: string;
  colYear: string;
  colTotal: string;
  rowSalaries: string;
  rowFringe: string;
  rowEquipment: string;
  rowTravel: string;
  rowSupplies: string;
  rowSubawards: string;
  rowParticipants: string;
  rowExcluded: string;
  rowDirect: string;
  rowBaseMtdc: string;
  rowBaseTdc: string;
  rowIndirect: string;
  rowTotal: string;
  copy: string;
  copied: string;
  downloadCsv: string;
  csvFile: string;
  noteReclassified: string;
  noteSubaward: string;
  noteNoRate: string;
}

type Key = keyof Omit<YearBudget, "year">;

const CATEGORY_ROWS: readonly [Key, keyof GrantResultLabels][] = [
  ["salaries", "rowSalaries"],
  ["fringe", "rowFringe"],
  ["equipment", "rowEquipment"],
  ["travel", "rowTravel"],
  ["supplies", "rowSupplies"],
  ["subawards", "rowSubawards"],
  ["participants", "rowParticipants"],
  ["excluded", "rowExcluded"],
];

/** The table as numbers: one row per budget line that has money in it, plus the totals. */
export function budgetTable(
  result: BudgetResult,
  labels: GrantResultLabels,
  baseIsMtdc: boolean,
  rate: number,
  fmt: (value: number) => string
): (string | number)[][] {
  const columns = [...result.years, result.totals];
  const head = [
    labels.colItem,
    ...result.years.map((row) => t(labels.colYear, { year: row.year })),
    labels.colTotal,
  ];
  const line = (key: Key, label: string) => [
    label,
    ...columns.map((column) => column[key]),
  ];
  const rows = CATEGORY_ROWS.filter(([key]) => result.totals[key] > 0).map(
    ([key, label]) => line(key, labels[label])
  );
  return [
    head,
    ...rows,
    line("direct", labels.rowDirect),
    line("base", baseIsMtdc ? labels.rowBaseMtdc : labels.rowBaseTdc),
    line("indirect", t(labels.rowIndirect, { rate: fmt(rate) })),
    line("total", labels.rowTotal),
  ];
}

export function GrantBudgetResults({
  result,
  labels,
  baseIsMtdc,
  rate,
}: {
  result: BudgetResult;
  labels: GrantResultLabels;
  baseIsMtdc: boolean;
  rate: number;
}) {
  const fmt = useToolNumber();
  const money = (value: number) => `${labels.currency}${fmt(value)}`;
  const table = budgetTable(result, labels, baseIsMtdc, rate, fmt);
  const shown = table.map((row, index) =>
    index === 0
      ? row
      : row.map((cell, column) => (column === 0 ? cell : money(Number(cell))))
  );
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard
          label={labels.statDirect}
          value={money(result.totals.direct)}
        />
        <StatCard
          label={labels.statIndirect}
          value={money(result.totals.indirect)}
        />
        <StatCard
          emphasis
          label={labels.statTotal}
          value={money(result.totals.total)}
        />
      </StatGrid>
      <DataTable head={shown[0].map(String)} rows={shown.slice(1)} />
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={labels.copied}
          label={labels.copy}
          text={toTsv(table)}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(labels.csvFile, toCsv(table), "text/csv;charset=utf-8")
          }
          type="button"
        >
          {labels.downloadCsv}
        </button>
      </div>
      {result.reclassified.length > 0 ? (
        <Notice>
          {t(labels.noteReclassified, {
            items: result.reclassified.join(", "),
          })}
        </Notice>
      ) : null}
      {result.subawardExcess.length > 0 && baseIsMtdc ? (
        <div className="space-y-1">
          <h3 className={WIDGET_H3}>{labels.rowSubawards}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {result.subawardExcess.map((item) => (
              <li key={item.label}>
                {t(labels.noteSubaward, {
                  name: item.label,
                  excess: money(item.excess),
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {rate === 0 ? <Notice>{labels.noteNoRate}</Notice> : null}
    </div>
  );
}
